import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Chat from '../Chat/Chat';
import './ReferralDashboard.css';

interface ReferralRequest {
  id: string;
  candidateName: string;
  candidateEmail: string;
  candidateAvatar?: string;
  resume?: string;
  message: string;
  timestamp: string;
  status: 'pending' | 'accepted' | 'declined';
  skills: string[];
  experience: string;
  position: string;
}

interface ReferralDashboardProps {
  companyId: string;
  companyName: string;
  darkMode?: boolean;
  onClose?: () => void;
}

const ReferralDashboard: React.FC<ReferralDashboardProps> = ({ 
  companyId, 
  companyName,
  darkMode = false,
  onClose 
}) => {
  const { isAuthenticated, user } = useAuth();
  const [referralRequests, setReferralRequests] = useState<ReferralRequest[]>([]);
  const [isEmployee, setIsEmployee] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<ReferralRequest | null>(null);

  useEffect(() => {
    // Mock data for demonstration
    setTimeout(() => {
      // Check if user is an employee (mock check)
      const mockIsEmployee = user?.email?.includes('@company.com') || Math.random() > 0.5;
      setIsEmployee(mockIsEmployee);

      if (mockIsEmployee) {
        // Load mock referral requests
        setReferralRequests([
          {
            id: '1',
            candidateName: 'John Doe',
            candidateEmail: 'john.doe@example.com',
            candidateAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=john',
            message: 'I am very interested in joining your team. I have 5 years of experience in React and TypeScript.',
            timestamp: new Date(Date.now() - 86400000).toISOString(),
            status: 'pending',
            skills: ['React', 'TypeScript', 'Node.js', 'GraphQL'],
            experience: '5 years',
            position: 'Senior Frontend Engineer'
          },
          {
            id: '2',
            candidateName: 'Jane Smith',
            candidateEmail: 'jane.smith@example.com',
            candidateAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=jane',
            message: 'Looking forward to contributing to your innovative projects. I specialize in scalable backend systems.',
            timestamp: new Date(Date.now() - 172800000).toISOString(),
            status: 'pending',
            skills: ['Python', 'Django', 'PostgreSQL', 'AWS'],
            experience: '7 years',
            position: 'Backend Engineer'
          }
        ]);
      }
      setLoading(false);
    }, 1000);
  }, [user]);

  const handleAcceptRequest = (requestId: string) => {
    setReferralRequests(prev => 
      prev.map(req => 
        req.id === requestId ? { ...req, status: 'accepted' } : req
      )
    );
  };

  const handleDeclineRequest = (requestId: string) => {
    setReferralRequests(prev => 
      prev.map(req => 
        req.id === requestId ? { ...req, status: 'declined' } : req
      )
    );
  };

  if (loading) {
    return (
      <div className={`referral-dashboard ${darkMode ? 'dark' : 'light'}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading referral dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={`referral-dashboard ${darkMode ? 'dark' : 'light'}`}>
        <div className="auth-required">
          <h3>Login Required</h3>
          <p>Please log in to view the referral dashboard.</p>
        </div>
      </div>
    );
  }

  if (!isEmployee) {
    return (
      <div className={`referral-dashboard ${darkMode ? 'dark' : 'light'}`}>
        <div className="blur-overlay">
          <div className="blur-content">
            <div className="locked-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M36 22H12a2 2 0 00-2 2v16a2 2 0 002 2h24a2 2 0 002-2V24a2 2 0 00-2-2zM16 22V14a8 8 0 0116 0v8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Employee Access Only</h3>
            <p>This dashboard is only available to verified employees of {companyName}.</p>
            <p className="hint">If you are an employee, please verify your work email to gain access.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`referral-dashboard ${darkMode ? 'dark' : 'light'}`}>
      <div className="dashboard-header">
        <h2>Referral Dashboard - {companyName}</h2>
        {onClose && (
          <button className="close-btn" onClick={onClose} aria-label="Close dashboard">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-value">{referralRequests.length}</div>
          <div className="stat-label">Total Requests</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {referralRequests.filter(r => r.status === 'pending').length}
          </div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {referralRequests.filter(r => r.status === 'accepted').length}
          </div>
          <div className="stat-label">Accepted</div>
        </div>
      </div>

      <div className="requests-list">
        <h3>Referral Requests</h3>
        {referralRequests.length === 0 ? (
          <div className="no-requests">
            <p>No referral requests yet.</p>
          </div>
        ) : (
          referralRequests.map(request => (
            <div key={request.id} className={`request-card ${request.status}`}>
              <div className="request-header">
                <div className="candidate-info">
                  <img 
                    src={request.candidateAvatar} 
                    alt={request.candidateName}
                    className="candidate-avatar"
                  />
                  <div className="candidate-details">
                    <h4>{request.candidateName}</h4>
                    <p>{request.position} • {request.experience}</p>
                  </div>
                </div>
                <div className={`status-badge ${request.status}`}>
                  {request.status}
                </div>
              </div>

              <div className="request-message">
                <p>{request.message}</p>
              </div>

              <div className="request-skills">
                {request.skills.map((skill, index) => (
                  <span key={index} className="skill-tag">{skill}</span>
                ))}
              </div>

              <div className="request-footer">
                <div className="request-time">
                  {new Date(request.timestamp).toLocaleDateString()}
                </div>
                {request.status === 'pending' && (
                  <div className="request-actions">
                    <button 
                      className="action-btn accept"
                      onClick={() => handleAcceptRequest(request.id)}
                    >
                      Accept
                    </button>
                    <button 
                      className="action-btn decline"
                      onClick={() => handleDeclineRequest(request.id)}
                    >
                      Decline
                    </button>
                    <button 
                      className="action-btn chat"
                      onClick={() => setSelectedRequest(request)}
                    >
                      Chat
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {selectedRequest && (
        <div className="chat-modal">
          <Chat
            recipientId={selectedRequest.id}
            recipientName={selectedRequest.candidateName}
            recipientAvatar={selectedRequest.candidateAvatar}
            recipientRole={selectedRequest.position}
            companyName={companyName}
            darkMode={darkMode}
            onClose={() => setSelectedRequest(null)}
          />
        </div>
      )}
    </div>
  );
};

export default ReferralDashboard;